import { useCallback, useEffect, useMemo, useState } from 'react'
import { useUser } from '../context/UserContext'
import {
  moraPreacordadasDesactivar,
  moraPreacordadasGuardar,
  moraPreacordadasListar,
  moraPreacordadasObtener,
} from '../api/apiConfig'
import './SimuladorMoraExcepcion.css'
import './SolicitudesExcepcionMora.css'
import './TasasMoraPreacordadas.css'

const TIPOS = [
  { value: 'FIJA', label: 'Fija (%)' },
  { value: 'TASA_NEGOCIO', label: 'Tasa negocio (TAS_NEG)' },
  { value: 'TASA_NEGOCIO_MAS', label: 'Tasa negocio + spread' },
  { value: 'TMC', label: 'TMC (Máxima)' },
]

const emptyTramo = () => ({
  dias_desde: 1,
  dias_hasta: '',
  tipo_tasa: 'FIJA',
  tasa_mensual: '',
  spread_mensual: '',
  prioridad: 10,
  etiqueta_origen: '',
})

const emptyForm = () => ({
  id: null,
  rut: '',
  dv: '',
  cliente_nombre: '',
  ejecutivo_nombre: '',
  vigente_desde: '',
  vigente_hasta: '',
  activo: true,
  observacion: '',
  origen: 'Mantenedor MGC',
  tramos: [emptyTramo()],
})

function formatTramoResumen(t) {
  const dias =
    t.dias_hasta == null
      ? `${t.dias_desde}+ días`
      : `${t.dias_desde}–${t.dias_hasta} días`
  if (t.tipo_tasa === 'FIJA') return `${dias}: ${Number(t.tasa_mensual).toFixed(2)}%`
  if (t.tipo_tasa === 'TASA_NEGOCIO') return `${dias}: T.Negocio`
  if (t.tipo_tasa === 'TASA_NEGOCIO_MAS')
    return `${dias}: T.Negocio + ${Number(t.spread_mensual).toFixed(2)}%`
  if (t.tipo_tasa === 'TMC') return `${dias}: TMC`
  return dias
}

export default function TasasMoraPreacordadas() {
  const { user } = useUser()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [soloActivos, setSoloActivos] = useState(true)
  const [toast, setToast] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(emptyForm())

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await moraPreacordadasListar(user, {
        q: q.trim() || undefined,
        solo_activos: soloActivos,
      })
      setItems(res?.items || [])
    } catch (e) {
      console.error(e)
      showToast(e?.message || 'Error al cargar tasas', 'err')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [user, q, soloActivos])

  useEffect(() => {
    load()
  }, [load])

  const filteredHint = useMemo(() => {
    if (loading) return 'Cargando…'
    return `${items.length} tasa${items.length === 1 ? '' : 's'}`
  }, [items.length, loading])

  const openCreate = () => {
    setForm(emptyForm())
    setModalOpen(true)
  }

  const openEdit = async (id) => {
    try {
      const res = await moraPreacordadasObtener(user, { id })
      const it = res?.item
      if (!it) throw new Error('No encontrada')
      setForm({
        id: it.id,
        rut: String(it.rut),
        dv: it.dv || '',
        cliente_nombre: it.cliente_nombre || '',
        ejecutivo_nombre: it.ejecutivo_nombre || '',
        vigente_desde: it.vigente_desde || '',
        vigente_hasta: it.vigente_hasta || '',
        activo: Boolean(it.activo),
        observacion: it.observacion || '',
        origen: it.origen || 'Mantenedor MGC',
        tramos: (it.tramos || []).map((t) => ({
          dias_desde: t.dias_desde,
          dias_hasta: t.dias_hasta == null ? '' : t.dias_hasta,
          tipo_tasa: t.tipo_tasa,
          tasa_mensual: t.tasa_mensual == null ? '' : t.tasa_mensual,
          spread_mensual: t.spread_mensual == null ? '' : t.spread_mensual,
          prioridad: t.prioridad ?? 10,
          etiqueta_origen: t.etiqueta_origen || '',
        })),
      })
      setModalOpen(true)
    } catch (e) {
      showToast(e?.message || 'No se pudo abrir', 'err')
    }
  }

  const updateTramo = (idx, patch) => {
    setForm((f) => ({
      ...f,
      tramos: f.tramos.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    }))
  }

  const addTramo = () => setForm((f) => ({ ...f, tramos: [...f.tramos, emptyTramo()] }))
  const removeTramo = (idx) =>
    setForm((f) => ({
      ...f,
      tramos: f.tramos.length <= 1 ? f.tramos : f.tramos.filter((_, i) => i !== idx),
    }))

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = {
        id: form.id,
        rut: Number(String(form.rut).replace(/\D/g, '')),
        dv: String(form.dv).trim().toUpperCase(),
        cliente_nombre: form.cliente_nombre.trim(),
        ejecutivo_nombre: form.ejecutivo_nombre.trim() || null,
        vigente_desde: form.vigente_desde || null,
        vigente_hasta: form.vigente_hasta || null,
        activo: form.activo,
        observacion: form.observacion.trim() || null,
        origen: form.origen || 'Mantenedor MGC',
        tramos: form.tramos.map((t) => ({
          dias_desde: Number(t.dias_desde),
          dias_hasta: t.dias_hasta === '' || t.dias_hasta == null ? null : Number(t.dias_hasta),
          tipo_tasa: t.tipo_tasa,
          tasa_mensual: t.tasa_mensual === '' ? null : Number(t.tasa_mensual),
          spread_mensual: t.spread_mensual === '' ? null : Number(t.spread_mensual),
          prioridad: Number(t.prioridad) || 100,
          etiqueta_origen: t.etiqueta_origen || null,
        })),
      }
      await moraPreacordadasGuardar(user, payload)
      showToast(form.id ? 'Tasa actualizada' : 'Tasa creada')
      setModalOpen(false)
      await load()
    } catch (e) {
      showToast(e?.message || 'Error al guardar', 'err')
    } finally {
      setSaving(false)
    }
  }

  const handleDesactivar = async (id, nombre) => {
    if (!window.confirm(`¿Desactivar la tasa de ${nombre}?`)) return
    try {
      await moraPreacordadasDesactivar(user, { id })
      showToast('Tasa desactivada')
      await load()
    } catch (e) {
      showToast(e?.message || 'Error al desactivar', 'err')
    }
  }

  return (
    <div className="mora-app exc-page tmp-page">
      {toast && (
        <div className={`mora-toast show ${toast.type === 'err' ? 'err' : ''}`}>{toast.msg}</div>
      )}

      <header className="mora-page-header">
        <div>
          <h1>Tasas de mora preacordadas</h1>
          <p>
            Clientes con tasa especial por política. Si aplica, el simulador no pide excepción
            manual: se aprueba solo y va a Aplicaciones.
          </p>
        </div>
        <button type="button" className="mora-btn primary tmp-header-btn" onClick={openCreate}>
          + Nueva tasa
        </button>
      </header>

      <section className="mora-card exc-card">
        <div className="exc-toolbar">
          <div className="exc-filters">
            <label>
              Buscar
              <input
                type="search"
                placeholder="RUT, cliente o ejecutivo…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </label>
            <label className="tmp-check-inline">
              <span>&nbsp;</span>
              <span className="tmp-check">
                <input
                  type="checkbox"
                  checked={soloActivos}
                  onChange={(e) => setSoloActivos(e.target.checked)}
                />
                Solo activas
              </span>
            </label>
          </div>
          <div className="exc-actions">
            <span className="tmp-hint">{filteredHint}</span>
            <button type="button" className="mora-btn outline" onClick={load} disabled={loading}>
              Actualizar
            </button>
          </div>
        </div>

        {loading ? (
          <div className="mora-empty">Cargando tasas…</div>
        ) : items.length === 0 ? (
          <div className="mora-empty">
            No hay tasas para mostrar. Ejecuta la carga inicial SQL si aún no hay datos.
          </div>
        ) : (
          <div className="mora-table-wrap">
            <table className="exc-table">
              <thead>
                <tr>
                  <th>RUT</th>
                  <th>Cliente</th>
                  <th>Ejecutivo</th>
                  <th>Tramos</th>
                  <th>Observación</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>
                      <strong>{it.rut_cli}</strong>
                    </td>
                    <td className="exc-left">
                      <div className="exc-cli">{it.cliente_nombre}</div>
                    </td>
                    <td className="exc-left">
                      {it.ejecutivo_nombre ? (
                        <div>
                          <div>{it.ejecutivo_nombre}</div>
                          {it.ejecutivo_lineas ? (
                            <div className="tmp-ejec-hint">Actual en líneas</div>
                          ) : null}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{it.n_tramos}</td>
                    <td className="exc-left tmp-cond">
                      {it.observacion ? (
                        <span title={it.observacion}>
                          {it.observacion.slice(0, 60)}
                          {it.observacion.length > 60 ? '…' : ''}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <span className={`exc-badge ${it.activo ? 'aprobada' : 'anulada'}`}>
                        {it.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="tmp-actions">
                      <button type="button" className="mora-link-btn" onClick={() => openEdit(it.id)}>
                        Ver / Editar
                      </button>
                      {it.activo && (
                        <button
                          type="button"
                          className="mora-link-btn tmp-link-danger"
                          onClick={() => handleDesactivar(it.id, it.cliente_nombre)}
                        >
                          Desactivar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {modalOpen && (
        <div className="exc-modal-backdrop" onClick={() => !saving && setModalOpen(false)}>
          <div className="exc-modal tmp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="exc-modal-header">
              <div>
                <h2>{form.id ? 'Editar tasa preacordada' : 'Nueva tasa preacordada'}</h2>
                <p>Define el RUT y los tramos por días de mora.</p>
              </div>
              <button
                type="button"
                className="mora-link-btn"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                Cerrar
              </button>
            </div>

            <div className="tmp-form-grid">
              <label>
                RUT (número)
                <input
                  value={form.rut}
                  onChange={(e) => setForm({ ...form, rut: e.target.value.replace(/\D/g, '') })}
                />
              </label>
              <label>
                DV
                <input
                  maxLength={1}
                  value={form.dv}
                  onChange={(e) => setForm({ ...form, dv: e.target.value.toUpperCase() })}
                />
              </label>
              <label className="tmp-span2">
                Cliente
                <input
                  value={form.cliente_nombre}
                  onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })}
                />
              </label>
              <label className="tmp-span2">
                Ejecutivo
                <input
                  value={form.ejecutivo_nombre}
                  onChange={(e) => setForm({ ...form, ejecutivo_nombre: e.target.value })}
                  placeholder="Se completa desde sf_lineas si existe el RUT"
                />
                <span className="tmp-field-hint">
                  Al listar/abrir se cruza con <code>COMERCIAL.sf_lineas</code> (ejecutivo actual).
                  El valor guardado sirve de respaldo si no hay match.
                </span>
              </label>
              <label>
                Vigente desde
                <input
                  type="date"
                  value={form.vigente_desde}
                  onChange={(e) => setForm({ ...form, vigente_desde: e.target.value })}
                />
              </label>
              <label>
                Vigente hasta
                <input
                  type="date"
                  value={form.vigente_hasta}
                  onChange={(e) => setForm({ ...form, vigente_hasta: e.target.value })}
                />
              </label>
              <label className="tmp-span2">
                Observación
                <textarea
                  rows={2}
                  value={form.observacion}
                  onChange={(e) => setForm({ ...form, observacion: e.target.value })}
                />
              </label>
              <label className="tmp-check tmp-span2">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                />
                Activa
              </label>
            </div>

            <h3 className="exc-section-title">Tramos por días de mora</h3>
            <div className="tmp-tramos">
              {form.tramos.map((t, idx) => (
                <div key={idx} className="tmp-tramo-row">
                  <label>
                    Desde
                    <input
                      type="number"
                      min={1}
                      value={t.dias_desde}
                      onChange={(e) => updateTramo(idx, { dias_desde: e.target.value })}
                    />
                  </label>
                  <label>
                    Hasta (vacío = ∞)
                    <input
                      type="number"
                      min={1}
                      value={t.dias_hasta}
                      onChange={(e) => updateTramo(idx, { dias_hasta: e.target.value })}
                    />
                  </label>
                  <label>
                    Tipo
                    <select
                      value={t.tipo_tasa}
                      onChange={(e) => updateTramo(idx, { tipo_tasa: e.target.value })}
                    >
                      {TIPOS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {t.tipo_tasa === 'FIJA' && (
                    <label>
                      Tasa %
                      <input
                        type="number"
                        step="0.01"
                        value={t.tasa_mensual}
                        onChange={(e) => updateTramo(idx, { tasa_mensual: e.target.value })}
                      />
                    </label>
                  )}
                  {t.tipo_tasa === 'TASA_NEGOCIO_MAS' && (
                    <label>
                      Spread %
                      <input
                        type="number"
                        step="0.01"
                        value={t.spread_mensual}
                        onChange={(e) => updateTramo(idx, { spread_mensual: e.target.value })}
                      />
                    </label>
                  )}
                  <label>
                    Etiqueta
                    <input
                      value={t.etiqueta_origen}
                      onChange={(e) => updateTramo(idx, { etiqueta_origen: e.target.value })}
                    />
                  </label>
                  <button type="button" className="mora-link-btn tmp-link-danger" onClick={() => removeTramo(idx)}>
                    Quitar
                  </button>
                  <div className="tmp-tramo-preview">
                    {formatTramoResumen({
                      ...t,
                      dias_hasta: t.dias_hasta === '' ? null : Number(t.dias_hasta),
                      tasa_mensual: Number(t.tasa_mensual) || 0,
                      spread_mensual: Number(t.spread_mensual) || 0,
                    })}
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className="mora-btn outline tmp-add-tramo" onClick={addTramo}>
              + Agregar tramo
            </button>

            <footer className="exc-modal-footer">
              <button
                type="button"
                className="mora-btn neutral"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                Cancelar
              </button>
              <button type="button" className="mora-btn primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  )
}
